// src/app/okta-auth.service.ts
import { Injectable, Inject } from '@angular/core';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { OKTA_AUTH } from '@okta/okta-angular';
import { IDToken, OktaAuth, Token, AccessToken } from '@okta/okta-auth-js';
import { UpdatePasswordService } from '@services/signup-process-service/update-password.service';
import { environment as ENV } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class OktaAuthService {
   // Guards against multiple concurrent signInWithRedirect() calls, which
  // regenerate/overwrite the PKCE codeVerifier in storage and cause
  // "Could not load PKCE codeVerifier from storage" on the /login/callback.
  private signInRedirectInFlight = false;
  // Guards against initialize()/handleLoginRedirect() running more than once
  // concurrently (e.g. if AppComponent is constructed more than once), which
  // also causes the codeVerifier to be consumed/cleared twice.
  private initializePromise: Promise<void> | null = null;
  // Guards against registering the tokenManager 'added' listener more than
  // once (e.g. both the APP_INITIALIZER and the callback component call
  // setupTokenListeners()), which would otherwise fire storeUser()/
  // storeAccessToken() multiple times per token.
  private tokenListenersRegistered = false;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private updatePasswordService: UpdatePasswordService,
    private acmicService: AcmicService
  ) { }

  async login(): Promise<void> {
    if (this.signInRedirectInFlight) {
      return;
    }
    this.signInRedirectInFlight = true;
    try {
      await this.oktaAuth.signInWithRedirect();
    } finally {
      // Only reset on failure; a successful call navigates away from the app.
      this.signInRedirectInFlight = false;
    }
  }

  async signInWithRedirect(options: Parameters<OktaAuth['signInWithRedirect']>[0] = {}): Promise<void> {
    if (this.signInRedirectInFlight) {
      console.warn('[Okta] signInWithRedirect already in progress, ignoring duplicate call.');
      return;
    }
    this.signInRedirectInFlight = true;
    try {
      await this.oktaAuth.signInWithRedirect(options);
    } finally {
      this.signInRedirectInFlight = false;
    }
  }

  async logout(): Promise<void> {
    const currentUser = localStorage.getItem('currentUser');
    const idToken = currentUser ? JSON.parse(currentUser).id.id : await this.oktaAuth.tokenManager.get('idToken');
    await this.oktaAuth.tokenManager.clear();
    if (this.acmicService.isACMIC())
      window.location.href = `${ENV.OKTA.AAA.ISSUER}/oauth2/default/v1/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${window.location.origin}`;
    else
      window.location.href = `${ENV.OKTA.MAPFRE.ISSUER}/oauth2/default/v1/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${window.location.origin}`;
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.oktaAuth.isAuthenticated();
  }

  async initialize(): Promise<void> {
     // Prevent concurrent initialize() calls (e.g. if AppComponent is
    // constructed more than once) from racing to consume/clear the same
    // PKCE codeVerifier, which throws AuthSdkError.
    if (this.initializePromise) {
      return this.initializePromise;
    }
    this.initializePromise = this.doInitialize();
    try {
      await this.initializePromise;
    } finally {
      this.initializePromise = null;
    }
  }

  private async doInitialize(): Promise<void> {
    //Setup listener for future token events
    this.setupTokenListeners();


    //Wait for redirect callback to complete if needed
    try {
      if (await this.oktaAuth.isLoginRedirect()) {
        console.log('[Okta] Handling login redirect...');
        await this.oktaAuth.handleLoginRedirect();
      }
    } catch (error) {
      // AuthSdkError: "Could not load PKCE codeVerifier from storage" happens
      // when the auth flow already completed (e.g. codeVerifier already
      // consumed by a prior exchange), storage was cleared (e.g. logout
      // called sessionStorage.clear()), or a stale/bookmarked/back-button
      // /authorize URL with an old code_challenge+state was reused, which
      // has no matching codeVerifier left in storage. This is expected PKCE
      // single-use behavior, not something we can recover the original
      // transaction from — so clear the stale callback URL and kick off a
      // brand new login flow rather than leaving the user stuck on a broken page.
      console.error('[Okta] Failed to handle login redirect, starting a new sign-in flow:', error);
      this.oktaAuth.tokenManager.clear();
      const cleanUrl = window.location.origin + '/login';
      window.history.replaceState(null, '', cleanUrl);
      await this.signInWithRedirect();
      return;
    }

    //Now it's safe to restore the session
    await this.restoreSession();
  }

  /**
   * Handles the /login/callback route. Use this from a custom callback
   * component instead of the bare @okta/okta-angular OktaCallbackComponent,
   * which has no recovery logic and just displays the raw AuthSdkError.
   *
   * "Could not load PKCE codeVerifier from storage" happens here when the
   * /authorize URL is reused/replayed (e.g. copied from the address bar,
   * browser back/forward, or a bookmarked link) after the matching
   * codeVerifier has already been consumed or storage was cleared by a
   * logout. That URL's code_challenge/state are single-use, so there is no
   * way to recover the original transaction — the correct resolution is to
   * clear the stale callback URL and start a brand new sign-in flow.
   */
  async handleCallback(): Promise<void> {
    this.setupTokenListeners();
    try {
      await this.oktaAuth.handleLoginRedirect();
      await this.restoreSession();
    } catch (error) {
      console.error('[Okta] handleLoginRedirect failed on /login/callback, starting a new sign-in flow:', error);
      this.oktaAuth.tokenManager.clear();
      const cleanUrl = window.location.origin + '/login';
      window.history.replaceState(null, '', cleanUrl);
      await this.signInWithRedirect();
    }
  }

  private isIDToken(token: Token): token is IDToken {
    return (
      token &&
      typeof token === 'object' &&
      'claims' in token &&
      'idToken' in token &&
      token.claims !== undefined
    );
  }

  //Setup token listener (should be called early in app startup)
  async setupTokenListeners(): Promise<void> {
    if (this.tokenListenersRegistered) {
      return;
    }
    this.tokenListenersRegistered = true;
    this.oktaAuth.tokenManager.on('added', async (key, token) => {
      console.log('Token added:', key, token);

      if (key === 'idToken' && this.isIDToken(token)) {
        await this.storeUser(token);
      }
      if (key === 'accessToken' && this.isAccessToken(token)) {
        await this.storeAccessToken(token.accessToken);
      }
    });
  }


  private isAccessToken(token: any): token is AccessToken {
    return 'accessToken' in token;
  }




  //Restore session on refresh or revisit
  async restoreSession(): Promise<void> {
    try {
      const token = await this.oktaAuth.tokenManager.get('idToken');
      if (token && this.isIDToken(token)) {
        await this.storeUser(token);
      }
    } catch (error) {
      console.error('Error restoring token:', error);
    }
  }

  // ✅ Store user data in localStorage and notify service
  async storeUser(token: IDToken): Promise<void> {
    const { claims, idToken } = token;

    if (claims?.email && claims?.nonce) {
      const userData = {
        username: claims.email,
        id: {
          id: idToken,
          nonce: claims.nonce,
        },
      };
      localStorage.setItem('currentUser', JSON.stringify(userData));
      await this.updatePasswordService.createLocalStorageUser('', claims.email, '');
      console.log('User data stored:', userData);
      console.log('Stored Data:', localStorage.getItem('currentUser'));
    } else {
      console.warn('ID token missing expected claims:', claims);
    }
  }


  async storeAccessToken(token: string): Promise<void> {
    localStorage.setItem('x-access-token', token);
  }


  async refreshTokenAfterEmailUpdate1(): Promise<void> {
    try {
      const currentUserFromStorage = JSON.parse(localStorage.getItem('currentUser'));

      const newTokens = await this.oktaAuth.token.getWithoutPrompt({
        scopes: ['openid', 'profile', 'email']
      });

      // await this.oktaAuth.tokenManager.setTokens(newTokens.tokens);

      const token = newTokens.tokens.idToken;
      if (token && this.isIDToken(token)) {
        //await this.storeUser(idToken); // Update localStorage with new email
        const { claims, idToken } = token;

        if (claims?.email && claims?.nonce) {
          const userData = {
            ...currentUserFromStorage,
            username: claims.email,
            id: {
              id: idToken,
              nonce: claims.nonce,
            }
          };
          localStorage.setItem('currentUser', JSON.stringify(userData));
        }

      }

      console.log('Token refreshed after email update.');
    } catch (error) {
      console.error('Failed to refresh token after email update:', error);
    }
  }


  async refreshTokenAfterEmailUpdate(): Promise<void> {
    try {
      const currentUserFromStorage = JSON.parse(localStorage.getItem('currentUser') || '{}');

      // Get new tokens (ID token + Access token)
      const newTokens = await this.oktaAuth.token.getWithoutPrompt({
        scopes: ['openid', 'profile', 'email']
      });

      const { idToken, accessToken } = newTokens.tokens;

      if (idToken && this.isIDToken(idToken)) {
        const { claims } = idToken;

        if (claims?.email && claims?.nonce) {
          const userData = {
            ...currentUserFromStorage,
            username: claims.email,
            id: {
              id: idToken.idToken, // raw ID token string
              nonce: claims.nonce,
            },
            accessToken: accessToken?.accessToken // raw Access token string
          };

          localStorage.setItem('currentUser', JSON.stringify(userData));
          localStorage.setItem('x-access-token', accessToken.accessToken);
        }
      }

      console.log('Tokens refreshed after email update.');
    } catch (error) {
      console.error('Failed to refresh tokens after email update:', error);
    }
  }

}